/* ============================================================================
 * SUPZ — Carrinho do Nutri
 * Página intermediária vista pelo PACIENTE.
 *
 * Markup: sections/nutri-cart-landing.liquid
 * Config: window.SUPZ, renderizado em layout/theme.liquid
 *
 * URL: /pages/carrinho?c=<token>&k=<key>&src=<qr|wa|copy>
 *
 * O id de carrinho da Storefront API é `gid://shopify/Cart/<token>?key=<key>`.
 * O par é quebrado em c= e k= para a URL não virar um blob percent-encoded —
 * o QR code fica bem menos denso assim. Aqui ele é remontado.
 *
 * DESTINO: /cart, não mais o checkout (13/10/2026). O checkout da Shopify não
 * deixa remover linha nem mudar quantidade; na /cart o paciente ajusta e
 * continua comprando. Ver "Destino do link: /cart" no CLAUDE.md.
 *
 * snippets/minicart.liquid:343 faz monkey-patch em window.fetch e chama
 * args[0].includes(...): toda URL aqui é string, nunca Request/URL.
 * ========================================================================== */

(() => {
  'use strict';

  const API_VERSION = '2025-10'; // manter em sincronia com assets/nutri-cart.js

  const CART_QUERY = `
    query supzCart($id: ID!) {
      cart(id: $id) {
        id
        checkoutUrl
        totalQuantity
        cost { totalAmount { amount currencyCode } }
        attributes { key value }
        discountCodes { code applicable }
      }
    }
  `;

  const CART_ATTRIBUTES_UPDATE = `
    mutation supzMarcarAbertura($cartId: ID!, $attributes: [AttributeInput!]!) {
      cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
        userErrors { field message }
      }
    }
  `;

  // -------------------------------------------------------------------------
  // Storefront API
  // -------------------------------------------------------------------------

  async function storefront(query, variables, opcoes) {
    const cfg = window.SUPZ || {};
    const shop = cfg.shopDomain || (window.Shopify && window.Shopify.shop);

    if (!cfg.storefrontToken) throw new Error('storefrontToken ausente');
    if (!shop) throw new Error('domínio da loja não resolvido');

    const res = await fetch(`https://${shop}/api/${API_VERSION}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': cfg.storefrontToken,
      },
      body: JSON.stringify({ query, variables }),
      keepalive: !!(opcoes && opcoes.keepalive),
    });

    const body = await res.json().catch(() => null);

    if (!res.ok) throw new Error(`Storefront API HTTP ${res.status}`);
    if (body && body.errors) throw new Error(`GraphQL: ${JSON.stringify(body.errors)}`);

    return body.data;
  }

  // -------------------------------------------------------------------------
  // parâmetros
  // -------------------------------------------------------------------------

  // tokens de carrinho da Shopify são alfanuméricos com hífen; a chave é hex.
  // Validar antes de montar o GID evita mandar lixo para a API e dá erro
  // amigável em vez de resposta estranha.
  const TOKEN_OK = /^[A-Za-z0-9-]+$/;
  const KEY_OK = /^[A-Za-z0-9]+$/;

  function lerParametros() {
    const q = new URLSearchParams(window.location.search);
    const c = (q.get('c') || '').trim();
    const k = (q.get('k') || '').trim();
    const src = (q.get('src') || '').trim().slice(0, 20);

    if (!c || !k || !TOKEN_OK.test(c) || !KEY_OK.test(k)) return null;

    return {
      token: c,
      key: k,
      cartId: `gid://shopify/Cart/${c}?key=${k}`,
      src,
      // Gancho de teste da queda para o checkout. Ver forcarFalha().
      falha: (q.get('falha') || '').trim(),
    };
  }

  // -------------------------------------------------------------------------
  // formatação
  // -------------------------------------------------------------------------

  function formatarDinheiro(amount, currencyCode) {
    const valor = Number(amount);
    if (!isFinite(valor)) return '';
    try {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: currencyCode || 'BRL',
      }).format(valor);
    } catch (e) {
      return `${currencyCode} ${valor.toFixed(2)}`;
    }
  }

  function textoItens(qtd) {
    return `${qtd} ${qtd === 1 ? 'item' : 'itens'}`;
  }

  function lerAtributo(attributes, chave) {
    const achado = (attributes || []).find((a) => a.key === chave);
    return achado ? achado.value : null;
  }

  // -------------------------------------------------------------------------
  // marcação de abertura
  //
  // cartAttributesUpdate SUBSTITUI a lista inteira de atributos. Reescrever só
  // os novos apagaria carrinho_original, nutricionista e criado_em — que são o
  // dado que motiva o projeto. Por isso a união é montada a partir do que já
  // veio na consulta.
  //
  // Só a PRIMEIRA abertura é gravada: se o paciente abrir de novo, a origem
  // original é a atribuição que interessa.
  //
  // Roda ANTES da troca do cookie e é aguardada: depois da troca este mesmo
  // carrinho passa a ser o da sessão do paciente, e a verificação lê os
  // atributos dele — a escrita precisa já ter chegado.
  // -------------------------------------------------------------------------

  async function marcarAbertura(cartId, attributes, src) {
    if (lerAtributo(attributes, 'aberto_em')) return;

    const uniao = (attributes || [])
      .filter((a) => a.key !== 'aberto_em' && a.key !== 'origem')
      .map((a) => ({ key: a.key, value: a.value }));

    uniao.push({ key: 'aberto_em', value: new Date().toISOString() });
    if (src) uniao.push({ key: 'origem', value: src });

    await storefront(
      CART_ATTRIBUTES_UPDATE,
      { cartId, attributes: uniao },
      { keepalive: true }
    );
  }

  // -------------------------------------------------------------------------
  // entrega na /cart
  //
  // O carrinho criado pelo cartCreate e o carrinho de sessão da loja são o
  // MESMO tipo de objeto: medido em 13/10/2026, o token de /cart.js abre na
  // Storefront API, e com o cookie `cart` apontando para um carrinho do
  // cartCreate, /cart.js devolve as linhas e os atributos dele.
  //
  // Então não se copia nada: o carrinho da nutri PASSA A SER o carrinho da
  // sessão do paciente. Linhas, assinaturas (selling plans), propriedades de
  // linha, cupom e atributos vão intactos, porque são o mesmo objeto.
  //
  // ⚠ O formato do cookie `cart` NÃO é documentado pela Shopify. É por isso
  // que a troca é VERIFICADA, nunca presumida: depois de gravar, /cart.js
  // precisa devolver o token da nutri, o `nutricionista` e o cupom. Qualquer
  // divergência restaura o cookie anterior e manda o paciente para o
  // checkoutUrl — o comportamento de antes desta mudança, que preserva a
  // atribuição. Se a Shopify mudar o cookie, o link degrada para o checkout;
  // não perde comissão em silêncio.
  //
  // Por que não o permalink /cart/<id>:<qtd>?storefront=true: a doc diz
  // "Selling plans don't work with cart permalinks" — assinatura viraria
  // compra avulsa.
  //
  // CARRINHO ANTERIOR DO PACIENTE: substituído, por decisão (13/10/2026). O
  // link representa o que a nutri montou. O estado anterior vai para a
  // telemetria (carrinho_anterior) para saber se isso é comum.
  // -------------------------------------------------------------------------

  // Espelha o cookie que a própria Shopify grava: valor URL-encoded, path=/,
  // ~90 dias, SameSite=Lax. Medido no Set-Cookie de /cart/add.js.
  const COOKIE_MAX_AGE = 60 * 60 * 24 * 90;

  function lerCookieCru(nome) {
    const par = document.cookie
      .split('; ')
      .find((c) => c.indexOf(nome + '=') === 0);
    return par ? par.slice(nome.length + 1) : null;
  }

  function gravarCookieCru(nome, valorCru) {
    document.cookie =
      `${nome}=${valorCru}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax` +
      (window.location.protocol === 'https:' ? '; Secure' : '');
  }

  function apagarCookie(nome) {
    document.cookie = `${nome}=; path=/; max-age=0; SameSite=Lax`;
  }

  async function lerCarrinhoDaSessao() {
    const raiz = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const res = await fetch(`${raiz}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!res.ok) throw new Error(`/cart.js HTTP ${res.status}`);
    return res.json();
  }

  const semChave = (token) => String(token || '').split('?')[0];

  // Códigos de desconto aplicados na sessão, em maiúsculas. A fonte que decide
  // é discount_codes: o cupom da nutri (ex.: HENRIQUE10) é desconto POR LINHA e
  // NÃO aparece em cart_level_discount_applications — medido no teste do pedido
  // #21705: com o cupom ativo, essa lista vem vazia. A leitura de
  // cart_level_discount_applications fica para um código de pedido.
  function codigosDaSessao(sessao) {
    const codigos = new Set();
    (sessao.discount_codes || []).forEach((d) => {
      if (d && d.code && d.applicable !== false) codigos.add(String(d.code).toUpperCase());
    });
    (sessao.cart_level_discount_applications || []).forEach((d) => {
      if (d && d.type === 'discount_code' && d.title) codigos.add(String(d.title).toUpperCase());
    });
    return codigos;
  }

  // O que precisa ser verdade na sessão para a /cart ser um destino seguro.
  // Devolve null se está tudo certo, ou o motivo da recusa.
  function conferirEntrega(sessao, params, cart) {
    if (semChave(sessao.token) !== params.token) return 'token_divergente';
    if (sessao.item_count !== cart.totalQuantity) return 'itens_divergentes';

    const nutri = lerAtributo(cart.attributes, 'nutricionista');
    const attrs = sessao.attributes || {};
    if (nutri && attrs.nutricionista !== nutri) return 'nutricionista_ausente';

    const esperados = (cart.discountCodes || [])
      .filter((d) => d.applicable)
      .map((d) => String(d.code).toUpperCase());
    const presentes = codigosDaSessao(sessao);
    if (esperados.some((c) => !presentes.has(c))) return 'cupom_ausente';

    return null;
  }

  // Gancho de teste: ?falha=cookie grava um token inválido no lugar do da
  // nutri, para exercitar a verificação e a queda para o checkout de verdade.
  // Inerte no tema publicado — só vale em tema não publicado (preview/dev).
  function forcarFalha(params) {
    const papel = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
    return params.falha === 'cookie' && !!papel && papel !== 'main';
  }

  async function entregarNaCart(params, cart) {
    const registro = {
      carrinho_anterior: 'desconhecido',
      itens_anteriores: '',
      resultado: 'checkout',
      motivo: '',
    };

    // estado anterior, para a telemetria
    try {
      const antes = await lerCarrinhoDaSessao();
      registro.itens_anteriores = String(antes.item_count);
      if (semChave(antes.token) === params.token) registro.carrinho_anterior = 'mesmo_carrinho';
      else registro.carrinho_anterior = antes.item_count > 0 ? 'com_itens' : 'vazio';
    } catch (e) {
      /* fica 'desconhecido' — não impede a entrega */
    }

    const cookieAntes = lerCookieCru('cart');
    const alvo = forcarFalha(params)
      ? encodeURIComponent(`invalido-${params.token}?key=${params.key}`)
      : encodeURIComponent(`${params.token}?key=${params.key}`);

    let motivo;
    try {
      gravarCookieCru('cart', alvo);
      motivo = conferirEntrega(await lerCarrinhoDaSessao(), params, cart);
    } catch (e) {
      motivo = 'erro_leitura';
    }

    if (!motivo) {
      registro.resultado = 'cart';
      registro.motivo = 'ok';
      return registro;
    }

    // Queda: devolve ao paciente o carrinho que ele tinha e segue pelo
    // checkout do carrinho da nutri, como antes.
    if (cookieAntes) gravarCookieCru('cart', cookieAntes);
    else apagarCookie('cart');

    console.warn('[supz] entrega na /cart recusada, seguindo pelo checkout:', motivo);
    registro.motivo = motivo;
    return registro;
  }

  // -------------------------------------------------------------------------
  // telemetria — evento "abertura" (webhook do Make, cenário 5836112)
  //
  // Mesmo transporte do assets/nutri-cart.js (GET + no-cors + keepalive; ver
  // o comentário de lá). Uma linha por abertura do link na aba "aberturas".
  //
  // ⚠ A rota "criacao" do router é o FALLBACK. Este evento só pode sair
  // depois de existir a rota "abertura" no cenário — sem ela, cada abertura
  // viraria uma linha falsa na Página1, que é o denominador.
  // -------------------------------------------------------------------------

  function enviarAbertura(landing, params, cart, primeira, registro) {
    const url = (window.SUPZ || {}).webhookUrl || landing.dataset.webhook;
    if (!url || url.indexOf('<<') === 0) return;

    const separador = url.indexOf('?') === -1 ? '?' : '&';
    const dados = new URLSearchParams({
      evento: 'abertura',
      cart_token: params.token,
      nutri_id: lerAtributo(cart.attributes, 'nutricionista') || '',
      criado_em: lerAtributo(cart.attributes, 'criado_em') || '',
      aberto_em: new Date().toISOString(),
      primeira_abertura: primeira ? 'sim' : 'nao',
      carrinho_anterior: registro.carrinho_anterior,
      itens_anteriores: registro.itens_anteriores,
      resultado: registro.resultado,
      motivo: registro.motivo,
    });

    try {
      fetch(url + separador + dados.toString(), {
        method: 'GET',
        mode: 'no-cors',
        keepalive: true,
      }).catch(() => {});
    } catch (e) {
      /* noop — telemetria nunca derruba o produto */
    }
  }

  // -------------------------------------------------------------------------
  // componente
  // -------------------------------------------------------------------------

  class NutriCartLanding extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;

      this.segundos = parseInt(this.dataset.seconds, 10);
      if (!isFinite(this.segundos)) this.segundos = 3;

      this.iniciar();
    }

    disconnectedCallback() {
      if (this.timer) clearInterval(this.timer);
    }

    mostrar(estado) {
      this.querySelectorAll('[data-estado]').forEach((bloco) => {
        bloco.hidden = bloco.dataset.estado !== estado;
      });
    }

    async iniciar() {
      const params = lerParametros();

      if (!params) {
        console.warn('[supz] link sem c= ou k= válidos');
        this.mostrar('erro');
        return;
      }

      let cart;
      try {
        const dados = await storefront(CART_QUERY, { id: params.cartId });
        cart = dados && dados.cart;
      } catch (erro) {
        console.error('[supz] falha ao carregar o carrinho:', erro);
        this.mostrar('erro');
        return;
      }

      // cart null = id inválido, carrinho expirado ou já convertido em pedido
      if (!cart || !cart.checkoutUrl || cart.totalQuantity === 0) {
        this.mostrar('erro');
        return;
      }

      const primeira = !lerAtributo(cart.attributes, 'aberto_em');
      this.render(cart);

      try {
        await marcarAbertura(params.cartId, cart.attributes, params.src);
      } catch (e) {
        // A marcação é telemetria do carrinho; não decide o destino.
        console.error('[supz] falha ao marcar abertura:', e);
      }

      const registro = await entregarNaCart(params, cart);
      enviarAbertura(this, params, cart, primeira, registro);

      const botao = this.querySelector('[data-checkout]');
      const destino = registro.resultado === 'cart' ? this.urlCart() : cart.checkoutUrl;
      botao.href = destino;
      if (registro.resultado === 'cart' && this.dataset.textoCart) {
        botao.textContent = this.dataset.textoCart;
      }
      this.contagem(destino);
    }

    urlCart() {
      const raiz = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return `${raiz}cart`;
    }

    render(cart) {
      const nome = lerAtributo(cart.attributes, 'nutricionista_nome');
      if (nome) this.querySelector('[data-nutri]').textContent = nome;

      this.querySelector('[data-itens]').textContent = textoItens(cart.totalQuantity);
      this.querySelector('[data-total]').textContent = formatarDinheiro(
        cart.cost.totalAmount.amount,
        cart.cost.totalAmount.currencyCode
      );

      // O destino só é conhecido depois da troca verificada; até lá o botão
      // aponta para o checkout, que é o destino seguro.
      this.querySelector('[data-checkout]').href = cart.checkoutUrl;

      this.mostrar('pronto');
    }

    contagem(destino) {
      const alvo = this.querySelector('[data-contagem]');

      if (this.segundos <= 0) {
        alvo.textContent = '';
        return;
      }

      let restante = this.segundos;

      const escrever = () => {
        alvo.textContent = `Redirecionando em ${restante}s...`;
      };

      escrever();

      this.timer = setInterval(() => {
        restante -= 1;

        if (restante <= 0) {
          clearInterval(this.timer);
          alvo.textContent = 'Redirecionando...';
          // replace: o paciente não deve voltar para esta página transitória
          window.location.replace(destino);
          return;
        }

        escrever();
      }, 1000);
    }
  }

  if (!customElements.get('nutri-cart-landing')) {
    customElements.define('nutri-cart-landing', NutriCartLanding);
  }
})();
